// GLOBAL-NAV-1 — the navigation spine.
//
// AtticusNW has no URL router; navigation is in-memory page state. This context
// wraps that model with a real history stack (Back/Forward), and a keep-alive
// page cache so every surface keeps its filters/sort/scroll on Back (the page
// components are never unmounted while "alive" — they're just hidden).
//
// A "location" = { page, params, key, entityType?, entityId?, title? }.
//   key = page id for singletons, `${page}:${entityId}` for entity views
//   (claim/invoice/contact/company). Keep-alive keeps all visited singletons
//   plus the most-recent ENTITY_CAP entity views (LRU); the current location is
//   always alive. Future pages participate via navRegistry — see GLOBAL_NAV_*.

import { createContext, useContext, useReducer, useState, useCallback, useEffect, useMemo } from "react";
import { apiFetch, can } from "../../api";
import BugReportModal from "../BugReportModal";

const NavCtx = createContext(null);
const ENTITY_CAP = 4;     // keep at most this many entity views mounted (LRU)
const STACK_CAP = 60;     // history length guard

function locKey(loc) {
  return loc.entityId != null ? `${loc.page}:${loc.entityId}` : loc.page;
}

// Recompute the alive set: current first, deduped by key, all singletons kept,
// entity views capped to the ENTITY_CAP most-recent. The current location is
// always included (it's prepended), so a Back into an evicted entity re-adds it.
function reviseAlive(prevAlive, current) {
  const merged = [current, ...prevAlive.filter((l) => l.key !== current.key)];
  const singles = merged.filter((l) => l.entityId == null);
  const entities = merged.filter((l) => l.entityId != null).slice(0, ENTITY_CAP);
  const out = [...singles, ...entities];
  // de-dupe defensively (current could be both)
  return out.filter((l, i, a) => a.findIndex((x) => x.key === l.key) === i);
}

function initState() {
  const home = { page: "dashboard", params: {}, key: "dashboard", ts: 0 };
  return { stack: [home], cursor: 0, alive: [home] };
}

function reducer(state, action) {
  const { stack, cursor } = state;
  switch (action.type) {
    case "navigate": {
      const loc = { ...action.loc, params: action.loc.params || {} };
      loc.key = locKey(loc);
      const cur = stack[cursor];
      // Re-navigating to the current location: update params in place, no dup.
      if (cur && cur.key === loc.key && !action.opts?.push) {
        const ns = stack.slice();
        ns[cursor] = { ...loc, ts: cur.ts };
        return { stack: ns, cursor, alive: reviseAlive(state.alive, ns[cursor]) };
      }
      let base = action.opts?.replace ? stack.slice(0, cursor) : stack.slice(0, cursor + 1);
      base.push({ ...loc, ts: (cur?.ts || 0) + 1 });
      if (base.length > STACK_CAP) base = base.slice(base.length - STACK_CAP);
      const nc = base.length - 1;
      return { stack: base, cursor: nc, alive: reviseAlive(state.alive, base[nc]) };
    }
    case "back": {
      const nc = Math.max(0, cursor - 1);
      return { stack, cursor: nc, alive: reviseAlive(state.alive, stack[nc]) };
    }
    case "forward": {
      const nc = Math.min(stack.length - 1, cursor + 1);
      return { stack, cursor: nc, alive: reviseAlive(state.alive, stack[nc]) };
    }
    case "go": {
      const nc = Math.max(0, Math.min(stack.length - 1, action.cursor));
      return { stack, cursor: nc, alive: reviseAlive(state.alive, stack[nc]) };
    }
    case "setLabel": {
      // An entity page reports its human label (e.g. claim #) once loaded; stamp
      // every history/alive entry for ITS OWN key (passed explicitly, since an
      // async load may finish while the user is on a different page).
      const key = action.key || state.stack[state.cursor].key;
      if (!key) return state;
      const patch = (l) => (l.key === key ? { ...l, params: { ...l.params, label: action.label } } : l);
      return { stack: state.stack.map(patch), cursor: state.cursor, alive: state.alive.map(patch) };
    }
    case "setSubLabel": {
      // A singleton page (Reports/Operations) reports its active sub-view name for
      // the breadcrumb (e.g. "Reports › WIP"); stamped on the current location only.
      const i = state.cursor;
      const cur = state.stack[i];
      if ((cur.params || {}).subLabel === action.text) return state;
      const ns = state.stack.slice();
      ns[i] = { ...cur, params: { ...cur.params, subLabel: action.text } };
      return { stack: ns, cursor: i, alive: state.alive.map((l) => (l.key === cur.key ? ns[i] : l)) };
    }
    case "replaceParams": {
      // REPORTS-UX-1: merge params into the CURRENT location in place — no history
      // push, no key change. The reports bridge uses this to keep the current
      // location's {report, view, label, subLabel} in sync with the live report
      // view so a pin/breadcrumb captures the exact state. Key is passed explicitly
      // (stale-write guard) mirroring setLabel; a late write for another page no-ops.
      const i = state.cursor;
      const cur = state.stack[i];
      if (action.key && cur.key !== action.key) return state;
      const ns = state.stack.slice();
      ns[i] = { ...cur, params: { ...cur.params, ...action.params } };
      return { stack: ns, cursor: i, alive: state.alive.map((l) => (l.key === cur.key ? ns[i] : l)) };
    }
    case "flush":
      return initState();
    default:
      return state;
  }
}

export function NavigationProvider({ userId, children }) {
  const [state, dispatch] = useReducer(reducer, undefined, initState);

  const navigate = useCallback((loc, opts) => dispatch({ type: "navigate", loc, opts }), []);
  const back = useCallback(() => dispatch({ type: "back" }), []);
  const forward = useCallback(() => dispatch({ type: "forward" }), []);
  const go = useCallback((c) => dispatch({ type: "go", cursor: c }), []);
  const reportLabel = useCallback((label, key) => { if (label) dispatch({ type: "setLabel", label, key }); }, []);
  const setSubLabel = useCallback((text) => dispatch({ type: "setSubLabel", text }), []);
  const replaceParams = useCallback((params, key) => dispatch({ type: "replaceParams", params, key }), []);

  // Flush keep-alive + history when the logged-in user changes (login / logout /
  // impersonation) so a stale page can't show another user's permission context.
  useEffect(() => { dispatch({ type: "flush" }); }, [userId]);

  // ── Server-persisted pins (favorites) + recents (last ~30 visits) ──
  const [pins, setPins] = useState([]);
  const [recents, setRecents] = useState([]);
  useEffect(() => {
    let ok = true;
    Promise.all([
      apiFetch("/nav/pins").catch(() => ({ pins: [] })),
      apiFetch("/nav/recents").catch(() => ({ recents: [] })),
    ]).then(([p, r]) => { if (ok) { setPins(p.pins || []); setRecents(r.recents || []); } });
    return () => { ok = false; };
  }, [userId]);

  const same = (a, kind, ref) => a.kind === kind && String(a.ref_id) === String(ref);
  const isPinned = useCallback((kind, ref) => pins.some((p) => same(p, kind, ref)), [pins]);
  const togglePin = useCallback((entry) => {
    if (pins.some((p) => same(p, entry.kind, entry.ref_id))) {
      setPins((ps) => ps.filter((p) => !same(p, entry.kind, entry.ref_id)));
      apiFetch(`/nav/pins?kind=${encodeURIComponent(entry.kind)}&ref_id=${encodeURIComponent(entry.ref_id)}`, { method: "DELETE" }).catch(() => {});
    } else {
      const tmp = { ...entry, id: `tmp-${entry.kind}-${entry.ref_id}` };
      setPins((ps) => [...ps, tmp]);
      apiFetch("/nav/pins", { method: "POST", body: entry })
        .then((row) => setPins((ps) => ps.map((p) => (p.id === tmp.id ? row : p)))).catch(() => {});
    }
  }, [pins]);
  const recordVisit = useCallback((entry) => {
    setRecents((rs) => [{ ...entry, id: `r-${entry.kind}-${entry.ref_id}` },
      ...rs.filter((r) => !same(r, entry.kind, entry.ref_id))].slice(0, 30));
    apiFetch("/nav/recents", { method: "POST", body: entry }).catch(() => {});
  }, []);
  const reorderPins = useCallback((orderedIds) => {
    setPins((ps) => orderedIds.map((id) => ps.find((p) => String(p.id) === String(id))).filter(Boolean));
    apiFetch("/nav/pins/reorder", { method: "PATCH", body: { order: orderedIds } }).catch(() => {});
  }, []);

  // ── Bug Spray launcher + open-count badge (BUG-SPRAY-1; BUG-7) ──
  // The 🐞 on the grey nav bar opens Bug Spray as a MODAL — GlobalNavBar reads openBug/bugUnread
  // from this context (it takes no props), and the modal renders here so it overlays the whole app
  // without a route change. BUG-7: the badge shows the count of OPEN items (always visible while any
  // bug is open), not the seen-aware unread count — Kuhrt wants a persistent "N open" indicator.
  // Poll on mount, on user change, and after the modal closes (status may have changed). FILE_BUG only.
  const [bugOpen, setBugOpen] = useState(false);
  const [bugUnread, setBugUnread] = useState(0);
  const refreshBugUnread = useCallback(() => {
    if (!can("file_bug")) { setBugUnread(0); return; }
    apiFetch("/bugs/unread-count").then((d) => setBugUnread(d.open || 0)).catch(() => {});
  }, []);
  useEffect(() => { refreshBugUnread(); }, [userId, refreshBugUnread]);
  const openBug = useCallback(() => setBugOpen(true), []);
  const closeBug = useCallback(() => { setBugOpen(false); refreshBugUnread(); }, [refreshBugUnread]);

  const value = useMemo(() => ({
    current: state.stack[state.cursor],
    stack: state.stack,
    cursor: state.cursor,
    alive: state.alive,
    canBack: state.cursor > 0,
    canForward: state.cursor < state.stack.length - 1,
    navigate, back, forward, go, reportLabel, setSubLabel, replaceParams,
    pins, recents, isPinned, togglePin, recordVisit, reorderPins,
    openBug, bugUnread,
  }), [state, navigate, back, forward, go, reportLabel, setSubLabel, replaceParams, pins, recents, isPinned, togglePin, recordVisit, reorderPins, openBug, bugUnread]);

  return (
    <NavCtx.Provider value={value}>
      {children}
      {bugOpen && <BugReportModal onClose={closeBug} onChanged={refreshBugUnread} />}
    </NavCtx.Provider>
  );
}

export function useNavigation() {
  const ctx = useContext(NavCtx);
  if (!ctx) throw new Error("useNavigation must be used within <NavigationProvider>");
  return ctx;
}
